import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IContentStateChangeResponseDto } from 'src/app/core/dtos/interfaces/i-content-state-change-response-dto';
import { INewContentVersionResponseDto } from 'src/app/core/dtos/interfaces/i-new-content-version-response-dto';
import { IRepurposeContentResponse } from 'src/app/core/dtos/interfaces/i-repurpose-content-response-dto';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { ISingleSponsorContentDto } from 'src/app/core/dtos/interfaces/i-single-sponsor-content-dto';
import { ISpikeContentResponseDto } from 'src/app/core/dtos/interfaces/i-spike-content-response-dto';
import { SingleSponsorContentDto } from 'src/app/core/dtos/single-sponsor-content-dto';
import { RestService } from 'src/app/shared/services/rest-service';

@Injectable({
  providedIn: 'root',
})
export class SingleSponsorContentTypeService {
  static readonly ENTITY_IDENTIFIER: string = 'singleSponsors';
  private readonly BASE_CONTENT_ROUTE = `api/${SingleSponsorContentTypeService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_CONTENT_ROUTE, ...args);

  constructor(private restService: RestService) {}

  create$(
    request: ISingleSponsorContentDto
  ): Observable<ISaveContentResponseDto> {
    return this.restService.post$<ISaveContentResponseDto>(
      this.BUILD_HTTP_ROUTE(),
      request
    );
  }

  createVersion$(
    contentId: string,
    versionId: string
  ): Observable<INewContentVersionResponseDto> {
    return this.restService.post$<INewContentVersionResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}/versions/${versionId}`),
      null
    );
  }

  getById$(
    contentId: string,
    versionId?: string,
    returnEvenDeleted: boolean = false
  ): Observable<ISingleSponsorContentDto> {
    const params = this.restService.buildHttpParamsObject({
      versionId: versionId,
      returnEvenDeleted: returnEvenDeleted,
    });
    return this.restService
      .get$<ISingleSponsorContentDto>(this.BUILD_HTTP_ROUTE(`${contentId}`), {
        params,
      })
      .pipe(map((x) => new SingleSponsorContentDto(x)));
  }

  getByVersionId$(
    versionId?: string,
    returnEvenDeleted: boolean = false
  ): Observable<ISingleSponsorContentDto> {
    const params = this.restService.buildHttpParamsObject({
      versionId: versionId,
      returnEvenDeleted: returnEvenDeleted,
    });
    return this.restService
      .get$<ISingleSponsorContentDto>(this.BUILD_HTTP_ROUTE(`-`), { params })
      .pipe(map((x) => new SingleSponsorContentDto(x)));
  }

  update$(
    contentId: string,
    contentVersion: ISingleSponsorContentDto
  ): Observable<ISaveContentResponseDto> {
    return this.restService.put$<ISaveContentResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}`),
      contentVersion
    );
  }

  delete$(
    contentId: string,
    overrideDeleteDate: Date = null
  ): Observable<Object> {
    const params = this.restService.buildHttpParamsObject({
      overrideDeleteDate: overrideDeleteDate,
    });
    return this.restService.remove$(this.BUILD_HTTP_ROUTE(`${contentId}`), {
      params,
    });
  }

  deleteVersion$(contentId: string, versionId: string): Observable<Object> {
    const params = {};
    return this.restService.remove$(
      this.BUILD_HTTP_ROUTE(`${contentId}/versions/${versionId}`),
      params
    );
  }

  spike$(
    contentId: string,
    reinstate: boolean = false
  ): Observable<ISpikeContentResponseDto> {
    const params = this.restService.buildHttpParamsObject({
      reinstate: reinstate,
    });
    return this.restService.put$<ISpikeContentResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}/spike`),
      { params }
    );
  }

  updateState$(
    versionId: string,
    status: string
  ): Observable<IContentStateChangeResponseDto> {
    return this.restService.put$<IContentStateChangeResponseDto>(
      this.BUILD_HTTP_ROUTE(`versions/${versionId}/state/${status}`),
      null
    );
  }

  repurpose$(
    versionId: string,
    initalState: string
  ): Observable<IRepurposeContentResponse> {
    const params = this.restService.buildHttpParamsObject({
      initialStateCode: initalState,
    });

    return this.restService.put$<IRepurposeContentResponse>(
      this.BUILD_HTTP_ROUTE(`versions/${versionId}/repurpose`),
      { params }
    );
  }
}
